import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Clock, Check, X, ChevronLeft, ChevronRight, Trophy, Building2, Target, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function QuizPlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const bundle = location.state?.bundle || null;
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const timerRef = useRef();

  const { data, isLoading } = useQuery({
    queryKey: ["quiz", id],
    queryFn: async () => (await api.get(`/quizzes/${id}`)).data,
  });
  const quiz = data?.quiz;

  const { data: bundlesData } = useQuery({
    queryKey: ["bundles"],
    queryFn: async () => (await api.get("/bundles")).data,
  });
  const resolvedBundle = bundle || (bundlesData?.bundles || []).find((b) => b.quiz_id === id) || null;

  // reset player state when navigating to a different quiz (e.g. section re-practice)
  useEffect(() => {
    setCurrent(0);
    setAnswers({});
    setElapsed(0);
    setResult(null);
    setSubmitting(false);
    setRetrying(false);
  }, [id]);

  useEffect(() => {
    if (quiz && !result) {
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [quiz, result]);

  if (isLoading || !quiz)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const questions = quiz.questions;
  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const submit = async () => {
    clearInterval(timerRef.current);
    setSubmitting(true);
    const answerArr = questions.map((_, i) => (answers[i] ?? -1));
    try {
      const { data: res } = await api.post(`/quizzes/${id}/submit`, { answers: answerArr, time_taken: elapsed });
      setResult(res);
    } catch {
      toast.error("Submission failed. Try again.");
      setSubmitting(false);
    }
  };

  if (result) {
    const s = result.submission;
    const sections = result.sections || [];
    const isBundle = result.is_bundle || !!resolvedBundle;
    const accent = resolvedBundle?.accent;
    const sourceQuizId = resolvedBundle?.quiz_id || id;
    const weakestSection = sections.length
      ? sections.reduce((min, s) => (s.score < min.score ? s : min), sections[0])
      : null;
    const retryWeakest = async () => {
      if (!weakestSection) return;
      setRetrying(true);
      try {
        const { data: res } = await api.post("/practice/section", { quiz_id: sourceQuizId, section: weakestSection.section });
        navigate(`/quizzes/${res.quiz_id}`);
      } catch {
        toast.error("Could not start practice. Try again.");
        setRetrying(false);
      }
    };
    return (
      <div className="max-w-2xl mx-auto" data-testid="quiz-result">
        {isBundle && resolvedBundle && (
          <div className="rounded-md border border-border bg-card overflow-hidden mb-6">
            <div className="h-2" style={{ background: accent }} />
            <div className="p-5 flex items-center gap-3">
              <div className="w-11 h-11 rounded-md flex items-center justify-center" style={{ background: `${accent}20` }}>
                <Building2 className="w-6 h-6" style={{ color: accent }} />
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{resolvedBundle.company} Mock Report</p>
                <h3 className="font-heading font-bold text-lg">{s.quiz_title}</h3>
              </div>
            </div>
          </div>
        )}
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tighter mb-2">{s.score}%</h1>
          <p className="text-muted-foreground">You got {s.correct} of {s.total} correct · {fmt(s.time_taken)}</p>
        </div>

        {sections.length > 0 && (
          <div className="p-6 rounded-md border border-border bg-card mb-6" data-testid="section-report">
            <h3 className="font-heading font-bold text-lg mb-4">Section Breakdown</h3>
            <div className="space-y-4">
              {sections.map((sec) => (
                <div key={sec.section} data-testid={`section-${sec.section.replace(/\s/g, "-")}`}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="font-medium">{sec.section}</span>
                    <span className="font-mono text-muted-foreground">{sec.correct}/{sec.total} · {sec.score}%</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full transition-[width] duration-500"
                      style={{ width: `${sec.score}%`, background: sec.score < 50 ? "hsl(var(--destructive))" : sec.score < 75 ? "hsl(var(--chart-4))" : "hsl(var(--success))" }} />
                  </div>
                </div>
              ))}
            </div>
            {weakestSection && (
              <div className="mt-5 pt-5 border-t border-border flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <Target className="w-4 h-4 text-destructive" />
                  <span>Weakest area: <span className="font-semibold">{weakestSection.section}</span> ({weakestSection.score}%)</span>
                </div>
                <Button onClick={retryWeakest} disabled={retrying} className="rounded-md gap-2" data-testid="retry-weak-section">
                  {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4" /> Re-practice {weakestSection.section}</>}
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="space-y-3 mb-8">
          {result.results.map((r, i) => (
            <div key={i} className="p-4 rounded-md border border-border bg-card" data-testid={`result-item-${i}`}>
              <div className="flex items-start gap-2 mb-2">
                {r.is_correct ? <Check className="w-5 h-5 text-success shrink-0 mt-0.5" /> : <X className="w-5 h-5 text-destructive shrink-0 mt-0.5" />}
                <div className="min-w-0">
                  {r.section && <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{r.section}</span>}
                  <p className="font-medium">{r.question}</p>
                </div>
              </div>
              <div className="pl-7 space-y-1 text-sm">
                {r.options.map((opt, oi) => (
                  <p key={oi} className={`${oi === r.correct ? "text-success font-medium" : oi === r.chosen ? "text-destructive line-through" : "text-muted-foreground"}`}>
                    {oi === r.correct ? "✓ " : oi === r.chosen ? "✗ " : "  "}{opt}
                  </p>
                ))}
                {r.explanation && <p className="text-muted-foreground italic mt-2 pt-2 border-t border-border">{r.explanation}</p>}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <Button onClick={() => navigate("/dashboard")} variant="outline" className="rounded-md" data-testid="result-dashboard">Go to Dashboard</Button>
          <Button onClick={() => navigate(isBundle ? "/bundles" : "/quizzes")} className="rounded-md" data-testid="result-more">{isBundle ? "More Mocks" : "More Assessments"}</Button>
        </div>
      </div>
    );
  }

  const q = questions[current];
  const answered = Object.keys(answers).length;

  return (
    <div className="max-w-3xl mx-auto" data-testid="quiz-player">
      {/* Floating timer bar */}
      <div className="sticky top-0 z-10 -mx-4 lg:-mx-8 px-4 lg:px-8 py-3 mb-8 bg-card/70 backdrop-blur-xl border-b border-border flex items-center justify-between">
        <Link to="/quizzes" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Exit
        </Link>
        <div className="flex items-center gap-2 font-mono font-bold" data-testid="quiz-timer">
          <Clock className="w-4 h-4 text-primary" /> {fmt(elapsed)}
        </div>
        <span className="text-sm font-mono text-muted-foreground">{answered}/{questions.length} answered</span>
      </div>

      <div className="h-1 rounded-full bg-secondary mb-8 overflow-hidden">
        <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${((current + 1) / questions.length) * 100}%` }} />
      </div>

      <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Question {current + 1} of {questions.length}</p>
      <h2 className="font-heading text-2xl font-bold mb-8 leading-tight" data-testid="quiz-question">{q.question}</h2>

      <div className="space-y-3 mb-8">
        {q.options.map((opt, oi) => (
          <button
            key={oi}
            onClick={() => setAnswers({ ...answers, [current]: oi })}
            data-testid={`option-${oi}`}
            className={`w-full text-left p-4 rounded-md border transition-colors flex items-center gap-3 ${
              answers[current] === oi ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-secondary"
            }`}
          >
            <span className={`w-7 h-7 rounded-md flex items-center justify-center text-sm font-mono font-bold shrink-0 ${answers[current] === oi ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
              {String.fromCharCode(65 + oi)}
            </span>
            {opt}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" className="rounded-md gap-1" disabled={current === 0} onClick={() => setCurrent(current - 1)} data-testid="prev-question">
          <ChevronLeft className="w-4 h-4" /> Previous
        </Button>
        {current < questions.length - 1 ? (
          <Button className="rounded-md gap-1" onClick={() => setCurrent(current + 1)} data-testid="next-question">
            Next <ChevronRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button className="rounded-md" onClick={submit} disabled={submitting} data-testid="submit-quiz">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Test"}
          </Button>
        )}
      </div>
    </div>
  );
}
